import React, { useState, useEffect, useRef } from 'react';
import { exportCSV, exportJSON, grade, gradeColor, importJSON, paidTotal, tuitionPaid, uid } from '../db';
import { getSessions, loadSessionData } from '../storage';
import {
  toast, Badge, Btn, Card, CardHead, Modal, ModalFooter,
  Field, Input, Select, Grid, Span, SecLabel, Search, Avatar, NoData, TblWrap,
} from '../ui.jsx';
import {
  buildCard, buildCardBack, printCard, printClassCards,
  printTC, printCC, buildTC, buildCC, printBonafide, buildBonafide,
} from '../print.js';
import { fmtDob } from '../utils/fmtDob';

function Reports({ db, save, activeSessionId }) {
  const [scope, setScope] = useState('all');
  const [filterCls, setFilterCls] = useState('');
  const [fmt, setFmt] = useState('csv');
  const [qPicker, setQPicker] = useState(null); // index of open quick-export picker
  const [prevSessionsData, setPrevSessionsData] = useState({}); // { [sid]: sdb }
  const [prevSessions, setPrevSessions] = useState([]);
  const [fields, setFields] = useState({
    name: true, uid: true, dob: false, blood: false,
    phone: true, address: false, email: false,
    cls: true, feeStatus: true, gpa: false, attendance: false,
    father: true, mother: false, caste: false, aadhar: false,
  });

  useEffect(() => {
    getSessions().then(all => {
      const others = all.filter(s => s.id !== activeSessionId);
      setPrevSessions(others);
      others.forEach(s => {
        loadSessionData(s.id).then(sdb => {
          if (sdb) setPrevSessionsData(prev => ({ ...prev, [s.id]: sdb }));
        });
      });
    });
  }, [activeSessionId]);
  const toggleField = k => setFields(f => ({ ...f, [k]: !f[k] }));
  const selCount = Object.values(fields).filter(Boolean).length;
  const allOn = Object.values(fields).every(Boolean);
  const toggleAll = () => { const v = !allOn; setFields(Object.fromEntries(Object.keys(fields).map(k => [k, v]))); };

  const all = Object.values(db.att).flatMap(d => Object.values(d));
  const attP = all.filter(v => v === 'P' || v === 'L').length, attA = all.filter(v => v === 'A').length;
  const attPct = attP + attA > 0 ? Math.round(attP / (attP + attA) * 100) : 0;
  const totalCol = db.pays.reduce((s, p) => s + p.amt, 0);

  const buildRows = () => {
    const stuList = scope === 'class' && filterCls
      ? db.students.filter(s => s.cls === filterCls)
      : db.students;
    return stuList.map(s => {
      const pd = paidTotal(db.pays, s.id);
      const row = {};
      if (fields.name) row['Name'] = s.fn + ' ' + s.ln;
      if (fields.uid) row['Student ID'] = s.id;
      if (fields.dob) row['DOB'] = s.dob || '';
      if (fields.blood) row['Blood Group'] = s.blood || '';
      if (fields.phone) row['Phone'] = s.fphone || '';
      if (fields.address) row['Address'] = s.city || '';
      if (fields.email) row['Email'] = '';
      if (fields.cls) row['Class'] = s.cls || '';
      if (fields.feeStatus) { row['Annual Fee'] = '₹' + s.fee; row['Paid'] = '₹' + pd; row['Balance'] = '₹' + (s.fee - pd); row['Status'] = s.fst; }
      if (fields.gpa) row['GPA'] = '—';
      if (fields.attendance) {
        let sp = 0, st = 0;
        Object.values(db.att).forEach(day => { if (day[s.id] !== undefined) { st++; if (day[s.id] === 'P' || day[s.id] === 'L') sp++; } });
        row['Attendance'] = st > 0 ? Math.round(sp / st * 100) + '%' : '—';
      }
      if (fields.father) row['Father'] = s.father || '';
      if (fields.mother) row['Mother'] = s.mother || '';
      if (fields.caste) row['Caste'] = s.caste || '';
      if (fields.aadhar) row['Aadhaar'] = s.aadhar || '';
      return row;
    });
  };

  // ── PDF helper — opens a styled print window ──────────────────
  const openPDF = (title, headers, dataRows) => {
    const school = db.settings.school || 'LORD KRISHNA PUBLIC SCHOOL';
    const year = db.settings.year || '2025-26';
    const addr = db.settings.addr || 'Ishapur, Laxminagar, Mathura';
    const thCells = headers.map(h => `<th>${h}</th>`).join('');
    const bodyRows = dataRows.map(r => `<tr>${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>${title}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;color:#181c1e;background:#fff;padding:24px}
  .hdr{display:flex;align-items:center;gap:16px;border-bottom:2px solid #002045;padding-bottom:12px;margin-bottom:16px}
  .hdr-text h1{font-size:16px;font-weight:800;color:#002045;letter-spacing:-0.02em}
  .hdr-text p{font-size:10px;color:#74777f;margin-top:2px}
  .badge{font-size:9px;font-weight:700;color:#1960a3;background:#e8f0fe;padding:3px 10px;border-radius:20px;margin-top:4px;display:inline-block}
  h2{font-size:13px;font-weight:700;color:#002045;margin-bottom:10px}
  table{width:100%;border-collapse:collapse;font-size:10px}
  th{background:#002045;color:#fff;padding:7px 10px;text-align:left;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:0.06em}
  td{padding:6px 10px;border-bottom:1px solid #f1f4f6;color:#181c1e}
  tr:nth-child(even) td{background:#f7fafc}
  tr:hover td{background:#eef2ff}
  .footer{margin-top:20px;font-size:9px;color:#74777f;text-align:right}
  @media print{body{padding:12px}button{display:none!important}}
</style></head><body>
<div class="hdr">
  <div class="hdr-text">
    <h1>${school}</h1>
    <p>${addr}</p>
    <span class="badge">${title} — ${year}</span>
  </div>
</div>
<h2>${title} <span style="font-weight:400;color:#74777f;font-size:11px">(${dataRows.length} records)</span></h2>
<table><thead><tr>${thCells}</tr></thead><tbody>${bodyRows}</tbody></table>
<div class="footer">Generated on ${new Date().toLocaleDateString('en-IN')} · ${school}</div>
<div style="margin-top:16px;text-align:center">
  <button onclick="window.print()" style="padding:10px 28px;background:#002045;color:#fff;border:none;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer">🖨 Print / Save as PDF</button>
</div>
</body></html>`;
    const w = window.open('', '_blank', 'width=1000,height=700');
    w.document.write(html); w.document.close();
  };

  const doExport = () => {
    const rows = buildRows();
    if (rows.length === 0) { toast('No students to export', 'err'); return; }
    const headers = Object.keys(rows[0]);
    const dataRows = rows.map(r => headers.map(h => r[h] || ''));
    if (fmt === 'json') {
      exportJSON({ students: rows, meta: { school: db.settings.school, year: db.settings.year, exported: new Date().toISOString() } });
      toast('JSON exported');
    } else if (fmt === 'pdf') {
      openPDF('Student Report', headers, dataRows);
      toast('PDF preview opened');
    } else {
      exportCSV('lkps_export.csv', [headers, ...dataRows]);
      toast('CSV exported');
    }
  };

  const fmtOptions = [
    { id: 'csv', icon: 'table_chart', label: 'CSV' },
    { id: 'json', icon: 'data_object', label: 'JSON' },
    { id: 'pdf', icon: 'picture_as_pdf', label: 'PDF' },
  ];

  const fieldGroups = [
    {
      label: 'Identity & Personal', fields: [
        { k: 'name', label: 'Full Name' }, { k: 'uid', label: 'Student ID' }, { k: 'dob', label: 'Date of Birth' }, { k: 'blood', label: 'Blood Group' },
      ]
    },
    {
      label: 'Contact Details', fields: [
        { k: 'phone', label: 'Primary Contact' }, { k: 'address', label: 'Address' }, { k: 'email', label: 'Guardian Email' },
      ]
    },
    {
      label: 'Academic & Financial', fields: [
        { k: 'cls', label: 'Class & Section' }, { k: 'feeStatus', label: 'Fee Status' }, { k: 'gpa', label: 'GPA (Annual)' }, { k: 'attendance', label: 'Attendance %' },
      ]
    },
    {
      label: 'Family Details', fields: [
        { k: 'father', label: 'Father Name' }, { k: 'mother', label: 'Mother Name' }, { k: 'caste', label: 'Caste' }, { k: 'aadhar', label: 'Aadhaar No.' },
      ]
    },
  ];

  const S = { // inline style helpers
    card: { background: 'var(--s1)', borderRadius: 16, padding: '28px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1.5px solid var(--br)' },
    iconBox: { width: 40, height: 40, borderRadius: 12, background: 'rgba(0,32,69,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    sectionTitle: { fontSize: 10, fontWeight: 800, color: 'var(--di)', textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 14, marginTop: 4 },
    radioRow: (active) => ({ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px', borderRadius: 12, border: `1.5px solid ${active ? '#1960a3' : 'var(--br)'}`, background: active ? 'var(--bll)' : 'var(--s1)', cursor: 'pointer', transition: 'all 160ms', marginBottom: 10 }),
    fmtBtn: (active) => ({ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '14px 10px', borderRadius: 12, border: `2px solid ${active ? '#1960a3' : 'var(--br)'}`, background: active ? 'var(--bll)' : 'var(--s1)', cursor: 'pointer', transition: 'all 160ms', flex: 1 }),
    fieldRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', transition: 'background 120ms' },
    chk: (on) => ({ width: 18, height: 18, borderRadius: 5, border: `2px solid ${on ? '#1960a3' : '#c4c6cf'}`, background: on ? '#1960a3' : 'var(--s2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 150ms' }),
  };

  return (
    <div className="reports-page" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#1960a3', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Data Management</div>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif', letterSpacing: '-0.02em', margin: '0 0 6px' }}>Export Data Configuration</h2>
        <p style={{ fontSize: 13, color: 'var(--di)', margin: 0 }}>Configure your data export requirements. Generate reports for institutional records or external audits.</p>
      </div>

      {/* Summary strip */}
      <div className="reports-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { icon: 'school', label: 'Students', val: db.students.length, color: '#1960a3' },
          { icon: 'record_voice_over', label: 'Teachers', val: db.teachers.length, color: '#059669' },
          { icon: 'payments', label: 'Total Collected', val: '₹' + totalCol.toLocaleString('en-IN'), color: '#cb9524' },
          { icon: 'fact_check', label: 'Avg Attendance', val: attPct + '%', color: '#7c3aed' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--s1)', borderRadius: 14, padding: '16px 18px', border: '1.5px solid var(--br)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: s.color + '14', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: s.color }}>{s.icon}</span>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif', lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 10, color: 'var(--di)', marginTop: 3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Config grid */}
      <div className="reports-config-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 20, marginBottom: 20 }}>

        {/* Left col */}
        <div className="reports-left-col" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Data Selection */}
          <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={S.iconBox}><span className="material-symbols-outlined" style={{ fontSize: 20, color: 'var(--bl)' }}>filter_alt</span></div>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif' }}>Data Selection</span>
            </div>
            <div style={S.radioRow(scope === 'all')} onClick={() => setScope('all')}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${scope === 'all' ? '#1960a3' : '#c4c6cf'}`, background: scope === 'all' ? '#1960a3' : 'var(--s2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {scope === 'all' && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--s1)' }} />}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--bl)' }}>Export All Students</div>
                <div style={{ fontSize: 11, color: 'var(--di)' }}>Includes all {db.students.length} enrolled students</div>
              </div>
            </div>
            <div style={S.radioRow(scope === 'class')} onClick={() => setScope('class')}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${scope === 'class' ? '#1960a3' : '#c4c6cf'}`, background: scope === 'class' ? '#1960a3' : 'var(--s2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {scope === 'class' && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--s1)' }} />}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--bl)' }}>Filter by Class</div>
                <div style={{ fontSize: 11, color: 'var(--di)' }}>Export a specific class only</div>
              </div>
            </div>
            {scope === 'class' && (
              <select value={filterCls} onChange={e => setFilterCls(e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--br)', background: 'var(--s2)', fontSize: 13, fontWeight: 600, color: 'var(--bl)', outline: 'none' }}>
                <option value="">— Select Class —</option>
                {db.classes.map(c => <option key={c.name}>{c.name}</option>)}
              </select>
            )}
          </div>

          {/* Format Selection */}
          <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={S.iconBox}><span className="material-symbols-outlined" style={{ fontSize: 20, color: 'var(--bl)' }}>file_present</span></div>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif' }}>Format Selection</span>
            </div>
            <div className="reports-format-grid" style={{ display: 'flex', gap: 10 }}>
              {fmtOptions.map(f => (
                <div key={f.id} style={S.fmtBtn(fmt === f.id)} onClick={() => setFmt(f.id)}>
                  <span className="material-symbols-outlined" style={{ fontSize: 22, color: fmt === f.id ? 'var(--sc)' : 'var(--di)', marginBottom: 6 }}>{f.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: fmt === f.id ? 'var(--sc)' : 'var(--di)' }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick exports */}
          <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={S.iconBox}><span className="material-symbols-outlined" style={{ fontSize: 20, color: 'var(--bl)' }}>bolt</span></div>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif' }}>Quick Exports</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                {
                  label: 'Students', icon: 'school',
                  headers: ['Name', 'Roll', 'Class', 'Father', 'Mother', 'Phone', 'Blood', 'DOB', 'Gender', 'Annual Fee', 'Paid', 'Balance', 'Status'],
                  getRows: () => db.students.map(s => { const pd = paidTotal(db.pays, s.id); return [s.fn + ' ' + s.ln, s.roll || '', s.cls, s.father || '', s.mother || '', s.fphone || '', s.blood || '', s.dob || '', s.gn || '', s.fee, pd, s.fee - pd, s.fst]; }),
                  fmts: ['csv', 'pdf', 'json'],
                },
                {
                  label: 'Teachers Directory', icon: 'record_voice_over',
                  headers: ['Name', 'EmpID', 'Subject', 'Class', 'Phone', 'Email', 'Salary', 'Status'],
                  getRows: () => db.teachers.map(t => [t.fn + ' ' + t.ln, t.empId || '', t.su || '', t.cls || '', t.ph || '', t.em || '', t.sal || 0, t.status || '']),
                  fmts: ['csv', 'pdf'],
                },
                {
                  label: 'Fee Ledger', icon: 'payments',
                  headers: ['Student', 'Class', 'Annual Fee', 'Paid', 'Balance', 'Status'],
                  getRows: () => db.students.map(s => { const pd = paidTotal(db.pays, s.id); return [s.fn + ' ' + s.ln, s.cls, '₹' + s.fee, '₹' + pd, '₹' + (s.fee - pd), s.fst]; }),
                  fmts: ['csv', 'pdf'],
                },
                {
                  label: 'Payment Transactions', icon: 'receipt_long',
                  headers: ['Date', 'Receipt', 'Student', 'Class', 'Type', 'Month', 'Amount', 'Method', 'Notes'],
                  getRows: () => db.pays.map(p => [p.dt, p.rc, p.nm || '', p.cls || '', p.ty || '', p.mn || '', p.amt, p.md || '', p.note || '']),
                  fmts: ['csv', 'pdf'],
                },
                {
                  label: 'Attendance Records', icon: 'fact_check',
                  headers: ['Date', 'Student', 'Class', 'Status'],
                  getRows: () => Object.entries(db.att).flatMap(([dt, day]) => Object.entries(day).map(([sid, v]) => { const s = db.students.find(x => x.id === sid); return s ? [dt, s.fn + ' ' + s.ln, s.cls, v === 'P' ? 'Present' : v === 'L' ? 'Leave' : 'Absent'] : null; }).filter(Boolean)),
                  fmts: ['csv', 'pdf'],
                },
                {
                  label: 'Grades / Marks', icon: 'quiz',
                  headers: ['Exam', 'Subject', 'Class', 'Student', 'Marks', 'Max', '%', 'Grade'],
                  getRows: () => db.exams.flatMap(e => { const cls = e.cls ? [e.cls] : db.classes.map(c => c.name); return cls.flatMap(c => { const key = e.id + '_' + c, sv = db.marks[key] || {}; return db.students.filter(s => s.cls === c).map(s => { const m = sv[s.id] !== undefined ? sv[s.id] : ''; return [e.name, e.su || '', c, s.fn + ' ' + s.ln, m, e.max, m !== '' ? Math.round(m / e.max * 100) + '%' : '—', m !== '' ? grade(m, e.max) : '—']; }); }); }),
                  fmts: ['csv', 'pdf'],
                },
                {
                  label: 'Full Backup', icon: 'save',
                  headers: [], getRows: () => [],
                  fmts: ['json'],
                  jsonFn: () => { exportJSON(db); toast('Backup exported'); },
                },
                {
                  label: 'Restore Backup', icon: 'restore',
                  headers: [], getRows: () => [],
                  fmts: ['restore'],
                  restoreFn: () => importJSON(data => { if (window.confirm('Replace ALL data with backup?')) { save({ ...db, ...data }); toast('Data restored'); } }),
                },
              ].map((q, i) => {
                const isOpen = qPicker === i;
                const fmtIcons = { csv: 'table_chart', pdf: 'picture_as_pdf', json: 'data_object', restore: 'upload_file' };
                const fmtLabels = { csv: 'CSV', pdf: 'PDF', json: 'JSON', restore: 'Import' };
                const runFmt = (f) => {
                  setQPicker(null);
                  if (f === 'restore') { q.restoreFn(); return; }
                  if (f === 'json' && q.jsonFn) { q.jsonFn(); return; }
                  const rows = q.getRows();
                  if (rows.length === 0 && f !== 'json') { toast('No data to export', 'err'); return; }
                  if (f === 'pdf') {
                    openPDF(q.label, q.headers, rows);
                    toast(q.label + ' PDF opened');
                  } else if (f === 'json') {
                    exportJSON({ data: rows, headers: q.headers, meta: { label: q.label, school: db.settings.school, year: db.settings.year, exported: new Date().toISOString() } });
                    toast(q.label + ' JSON exported');
                  } else {
                    exportCSV('lkps_' + q.label.toLowerCase().replace(/\s+/g, '_') + '.csv', [q.headers, ...rows]);
                    toast(q.label + ' CSV exported');
                  }
                };
                return (
                  <div key={i} style={{ borderRadius: 10, border: `1.5px solid ${isOpen ? '#1960a3' : 'var(--br)'}`, overflow: 'hidden', transition: 'border-color 150ms' }}>
                    <button onClick={() => setQPicker(isOpen ? null : i)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: isOpen ? 'var(--bll)' : 'var(--s2)', cursor: 'pointer', width: '100%', border: 'none', textAlign: 'left', transition: 'background 150ms' }}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'var(--s2)'; }}
                      onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'var(--s2)'; }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#1960a3' }}>{q.icon}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--bl)', flex: 1 }}>{q.label}</span>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#1960a3', transition: 'transform 200ms', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>arrow_forward</span>
                    </button>
                    {isOpen && (
                      <div className="reports-quick-format-row" style={{ display: 'flex', gap: 6, padding: '10px 14px', background: 'var(--s2)', borderTop: '1px solid var(--br)' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--di)', alignSelf: 'center', marginRight: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Format:</span>
                        {q.fmts.map(f => (
                          <button key={f} onClick={() => runFmt(f)}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1.5px solid var(--sc)', background: 'var(--s1)', color: 'var(--sc)', fontWeight: 700, fontSize: 11, cursor: 'pointer', transition: 'all 130ms' }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#1960a3'; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#1960a3'; }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{fmtIcons[f]}</span>
                            {fmtLabels[f]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right col — Field Selection */}
        <div className="reports-field-card" style={{ ...S.card, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={S.iconBox}><span className="material-symbols-outlined" style={{ fontSize: 20, color: 'var(--bl)' }}>checklist</span></div>
              <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif' }}>Field Selection</span>
            </div>
            <button onClick={toggleAll} style={{ fontSize: 12, fontWeight: 700, color: '#1960a3', border: 'none', background: 'transparent', cursor: 'pointer' }}>
              {allOn ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {fieldGroups.map((grp, gi) => (
            <div key={gi} style={{ marginBottom: 18, paddingBottom: gi < fieldGroups.length - 1 ? 18 : 0, borderBottom: gi < fieldGroups.length - 1 ? '1px solid #f1f4f6' : 'none' }}>
              <div style={S.sectionTitle}>{grp.label}</div>
              <div className="reports-field-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {grp.fields.map(f => (
                  <label key={f.k} style={{ ...S.fieldRow, background: fields[f.k] ? '#f0f4ff' : 'transparent' }}
                    onMouseEnter={e => { if (!fields[f.k]) e.currentTarget.style.background = 'var(--s2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = fields[f.k] ? '#f0f4ff' : 'transparent'; }}>
                    <input type="checkbox" checked={fields[f.k]} onChange={() => toggleField(f.k)} style={{ display: 'none' }} />
                    <div style={S.chk(fields[f.k])}>
                      {fields[f.k] && <span className="material-symbols-outlined" style={{ fontSize: 12, color: '#fff' }}>check</span>}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: fields[f.k] ? 'var(--bl)' : 'var(--mu)' }}>{f.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {/* Summary banner */}
          <div style={{ marginTop: 'auto', paddingTop: 16 }}>
            <div style={{ background: 'var(--s2)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#1960a3' }}>info</span>
              <div style={{ fontSize: 12, color: 'var(--mu)' }}>
                <span style={{ fontWeight: 700, color: 'var(--bl)' }}>{selCount} field{selCount !== 1 ? 's' : ''} selected</span>
                {' · '}
                {scope === 'class' && filterCls ? `${db.students.filter(s => s.cls === filterCls).length} students` : `${db.students.length} students`}
                {' · '}
                Format: <span style={{ fontWeight: 700, color: 'var(--bl)' }}>{fmt.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Previous Sessions Export */}
      {(() => {
        const allSessions = prevSessions;
        if (allSessions.length === 0) return null;
        return (
          <div className="reports-session-card" style={{ background: 'var(--s1)', borderRadius: 16, padding: '24px 28px', border: '1.5px solid var(--br)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(0,32,69,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: 'var(--bl)' }}>history</span>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--bl)', fontFamily: 'Manrope,sans-serif' }}>Previous Sessions</div>
                <div style={{ fontSize: 11, color: 'var(--di)', marginTop: 1 }}>Export data from other academic sessions</div>
              </div>
            </div>
            <div className="reports-session-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
              {allSessions.map(s => {
                const sdb = prevSessionsData[s.id];
                const stuCount = sdb?.students?.length || 0;
                const col = sdb?.pays?.reduce((sum, p) => sum + p.amt, 0) || 0;
                return (
                  <div key={s.id} style={{ padding: '14px 16px', borderRadius: 12, border: '1.5px solid var(--br)', background: 'var(--s2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--bl)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#fff' }}>school</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--bl)' }}>{s.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--di)' }}>{stuCount} students · ₹{col.toLocaleString('en-IN')}</div>
                      </div>
                    </div>
                    <div className="reports-session-actions" style={{ display: 'flex', gap: 6 }}>
                      {[
                        {
                          label: 'Students CSV', fn: () => {
                            if (!sdb?.students?.length) { toast('No data', 'err'); return; }
                            exportCSV('lkps_' + s.year + '_students.csv', [
                              ['Name', 'Roll', 'Class', 'Father', 'Phone', 'Annual Fee', 'Paid', 'Balance', 'Status'],
                              ...sdb.students.map(st => { const pd = paidTotal(sdb.pays || [], st.id); return [st.fn + ' ' + st.ln, st.roll || '', st.cls, st.father || '', st.fphone || '', st.fee, pd, st.fee - pd, st.fst]; })
                            ]); toast(s.name + ' students exported');
                          }
                        },
                        {
                          label: 'Fee PDF', fn: () => {
                            if (!sdb?.students?.length) { toast('No data', 'err'); return; }
                            const rows = sdb.students.map(st => { const pd = paidTotal(sdb.pays || [], st.id); return [st.fn + ' ' + st.ln, st.cls, '₹' + st.fee, '₹' + pd, '₹' + (st.fee - pd), st.fst]; });
                            openPDF(s.name + ' Fee Ledger', ['Student', 'Class', 'Annual Fee', 'Paid', 'Balance', 'Status'], rows);
                          }
                        },
                        { label: 'Backup', fn: () => { exportJSON({ ...sdb, _session: s }); toast(s.name + ' backup exported'); } },
                      ].map((btn, bi) => (
                        <button key={bi} onClick={btn.fn}
                          style={{ flex: 1, padding: '6px 4px', borderRadius: 8, border: '1.5px solid var(--br)', background: 'var(--s1)', color: '#1960a3', fontWeight: 700, fontSize: 10, cursor: 'pointer', transition: 'all 130ms' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; e.currentTarget.style.borderColor = '#1960a3'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = 'var(--br)'; }}>
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Sticky action bar */}
      <div className="reports-action-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', padding: '16px 24px', borderRadius: 16, border: '1.5px solid var(--br)', position: 'sticky', bottom: 16 }}>
        <div className="reports-action-summary" style={{ marginRight: 'auto', fontSize: 12, color: 'var(--di)' }}>
          <span style={{ fontWeight: 700, color: 'var(--bl)' }}>{selCount} field{selCount !== 1 ? 's' : ''} selected</span>
          {' · '}
          {scope === 'class' && filterCls ? `${db.students.filter(s => s.cls === filterCls).length} students` : `${db.students.length} students`}
          {' · '}
          <span style={{ fontWeight: 700, color: '#1960a3' }}>{fmt.toUpperCase()}</span>
        </div>
        <button onClick={() => importJSON(data => { if (window.confirm('Replace ALL data with backup?')) { save({ ...db, ...data }); toast('Data restored'); } })}
          style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid var(--br)', background: 'transparent', color: 'var(--di)', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 150ms' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#002045'; e.currentTarget.style.borderColor = '#002045'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#74777f'; e.currentTarget.style.borderColor = 'var(--br)'; }}>
          Restore Backup
        </button>
        <button onClick={doExport}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#002045,#1960a3)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,32,69,0.25)', transition: 'all 150ms', fontFamily: 'Manrope,sans-serif' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,32,69,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,32,69,0.25)'; }}>
          {fmt === 'pdf' ? 'Generate PDF' : fmt === 'json' ? 'Export JSON' : 'Export CSV'}
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{fmt === 'pdf' ? 'picture_as_pdf' : fmt === 'json' ? 'data_object' : 'download'}</span>
        </button>
      </div>
    </div>
  );
}


export default Reports;
